"use client";

import { loadStripe, type Stripe, type StripeEmbeddedCheckout } from "@stripe/stripe-js";

export const STRIPE_EMBEDDED_CHECKOUT_MOUNT_ID = "stripe-embedded-checkout-mount";
export const STRIPE_EMBEDDED_CHECKOUT_INNER_ID = "stripe-embedded-checkout-inner";

const STRIPE_CHECKOUT_DISMISS_SELECTOR = "[data-stripe-checkout-dismiss]";

let stripePromise: Promise<Stripe | null> | null = null;
let activeEmbeddedCheckout: StripeEmbeddedCheckout | null = null;
let previouslyFocusedElement: HTMLElement | null = null;
let checkoutEscapeHandler: ((e: KeyboardEvent) => void) | null = null;
let checkoutClickOutsideHandler: ((e: MouseEvent) => void) | null = null;
let checkoutOverlay: HTMLElement | null = null;
let checkoutIframeObserver: MutationObserver | null = null;

let bodyScrollLockCount = 0;
let savedBodyScrollY = 0;

function lockBodyScroll(): void {
  if (typeof document === "undefined") return;
  bodyScrollLockCount += 1;
  if (bodyScrollLockCount > 1) return;

  savedBodyScrollY = window.scrollY;
  const { body, documentElement: html } = document;
  body.style.position = "fixed";
  body.style.top = `-${savedBodyScrollY}px`;
  body.style.left = "0";
  body.style.right = "0";
  body.style.width = "100%";
  body.style.overflow = "hidden";
  html.style.overflow = "hidden";
}

function unlockBodyScroll(): void {
  if (typeof document === "undefined") return;
  bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
  if (bodyScrollLockCount > 0) return;

  const { body, documentElement: html } = document;
  body.style.position = "";
  body.style.top = "";
  body.style.left = "";
  body.style.right = "";
  body.style.width = "";
  body.style.overflow = "";
  html.style.overflow = "";
  window.scrollTo(0, savedBodyScrollY);
}

function getPublishableKey(): string {
  const key = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY?.trim();
  if (!key) {
    throw new Error("NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not configured");
  }
  return key;
}

export function getStripeJs(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = loadStripe(getPublishableKey());
  }
  return stripePromise;
}

export function getOrCreateStripeCheckoutMountElement(): HTMLElement {
  if (typeof document === "undefined") {
    throw new Error("Stripe checkout mount requires a browser DOM");
  }

  let element = document.getElementById(STRIPE_EMBEDDED_CHECKOUT_MOUNT_ID);
  if (!element) {
    element = document.createElement("div");
    element.id = STRIPE_EMBEDDED_CHECKOUT_MOUNT_ID;
    element.setAttribute("data-stripe-embedded-checkout", "true");
    document.body.appendChild(element);
  }
  return element;
}

/**
 * Creates an invisible, focusable "focus guard" sentinel. Used to wrap the
 * checkout content so the browser's native tab order can be looped back on
 * itself at the edges — the only way to contain focus around a cross-origin
 * iframe, which JS cannot reach into.
 */
function createCheckoutFocusGuard(): HTMLElement {
  const guard = document.createElement("span");
  guard.tabIndex = 0;
  guard.setAttribute("aria-hidden", "true");
  guard.setAttribute("data-stripe-checkout-focus-guard", "true");
  guard.style.cssText = [
    "position: fixed",
    "width: 1px",
    "height: 1px",
    "padding: 0",
    "margin: -1px",
    "overflow: hidden",
    "clip: rect(0 0 0 0)",
    "white-space: nowrap",
    "border: 0",
    "outline: none",
  ].join(";");
  return guard;
}

export interface PrepareStripeCheckoutMountOptions {
  onDismiss?: () => void;
}

export function prepareStripeCheckoutMountElement(
  options?: PrepareStripeCheckoutMountOptions
): HTMLElement {
  const overlay = getOrCreateStripeCheckoutMountElement();
  overlay.replaceChildren();
  checkoutOverlay = overlay;
  deactivateAccessibilityHandlers();

  overlay.style.cssText = [
    "position: fixed",
    "inset: 0",
    "z-index: 9999",
    "background: rgba(15, 23, 42, 0.55)",
    "padding: clamp(0.75rem, 3vw, 1.5rem)",
    "box-sizing: border-box",
    "overflow-x: hidden",
    "overflow-y: auto",
    "overscroll-behavior: contain",
    "-webkit-overflow-scrolling: touch",
  ].join(";");
  overlay.hidden = false;
  overlay.style.display = "block";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Checkout");
  overlay.style.pointerEvents = "auto";
  previouslyFocusedElement = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  lockBodyScroll();

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.setAttribute("data-stripe-checkout-dismiss", "true");
  closeButton.setAttribute("aria-label", "Close checkout");
  closeButton.textContent = "×";
  closeButton.style.cssText = [
    "position: fixed",
    "top: clamp(0.75rem, 3vw, 1.5rem)",
    "right: clamp(0.75rem, 3vw, 1.5rem)",
    "z-index: 10001",
    "width: 2.5rem",
    "height: 2.5rem",
    "border: none",
    "border-radius: 9999px",
    "background: rgba(255, 255, 255, 0.95)",
    "color: #0f172a",
    "font-size: 1.5rem",
    "line-height: 1",
    "cursor: pointer",
    "display: flex",
    "align-items: center",
    "justify-content: center",
    "box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15)",
    "outline-offset: 2px",
    "transition: box-shadow 0.15s ease",
  ].join(";");

  closeButton.addEventListener("focus", () => {
    closeButton.style.outline = "3px solid #2563eb";
  });
  closeButton.addEventListener("blur", () => {
    closeButton.style.outline = "none";
  });

  if (options?.onDismiss) {
    closeButton.addEventListener("click", options.onDismiss, { once: true });
  }

  const wrapper = document.createElement("div");
  wrapper.style.cssText = [
    "min-height: 100%",
    "display: flex",
    "flex-direction: column",
    "align-items: center",
    "box-sizing: border-box",
  ].join(";");

  const inner = document.createElement("div");
  inner.id = STRIPE_EMBEDDED_CHECKOUT_INNER_ID;
  inner.style.cssText = [
    "width: 500px",
    "max-width: 100%",
    "padding-bottom: 44px",
    "margin-top: auto",
    "margin-bottom: auto",
    "flex-shrink: 0",
    "padding-top: 0.75rem",
    "border-radius: 0.75rem",
    "background: #fff",
    "box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.35)",
  ].join(";");

  wrapper.append(inner);

  // Focus containment around a cross-origin iframe.
  //
  // The Stripe checkout renders inside a cross-origin <iframe>. A library focus
  // trap (focus-trap / tabbable) does NOT treat such an iframe as a tabbable
  // stop, so it cannot move focus into it — it just pins focus on the close
  // button. The browser's own tab order, however, traverses into and out of the
  // iframe correctly (the same-origin restriction blocks JavaScript, not the
  // browser's native focus handling).
  //
  // So we let native tab order do the work and only intercept at the two edges
  // with invisible focus-guard sentinels. DOM / tab order is:
  //   guardStart → [iframe fields] → closeButton → guardEnd
  // The close button is position:fixed, so placing it after the iframe in the
  // DOM does not change where it renders.
  const guardStart = createCheckoutFocusGuard();
  const guardEnd = createCheckoutFocusGuard();
  overlay.append(guardStart, wrapper, closeButton, guardEnd);

  const focusFirstCheckoutElement = (): void => {
    const iframe = inner.querySelector("iframe");
    if (iframe) {
      iframe.focus();
    } else {
      closeButton.focus();
    }
  };

  // Tab forward past the last element (close button) lands on guardEnd → wrap
  // to the first element (the iframe).
  guardEnd.addEventListener("focus", focusFirstCheckoutElement);
  // Shift+Tab backward before the first element (the iframe) lands on guardStart
  // → wrap to the last element (the close button).
  guardStart.addEventListener("focus", () => closeButton.focus());

  // Move initial focus to the first element inside the iframe as soon as Stripe
  // mounts it. The iframe is inserted asynchronously (after this function
  // returns, in mountEmbeddedCheckout), so we watch for its insertion.
  checkoutIframeObserver = new MutationObserver(() => {
    const iframe = inner.querySelector("iframe");
    if (!iframe) return;
    checkoutIframeObserver?.disconnect();
    checkoutIframeObserver = null;
    iframe.focus();
  });
  checkoutIframeObserver.observe(inner, { childList: true, subtree: true });

  checkoutEscapeHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      options?.onDismiss?.();
    }
  };
  document.addEventListener("keydown", checkoutEscapeHandler);

  checkoutClickOutsideHandler = (e: MouseEvent) => {
    if (e.target === overlay) {
      options?.onDismiss?.();
    }
  };
  overlay.addEventListener("click", checkoutClickOutsideHandler);

  return inner;
}

export function destroyEmbeddedCheckout(): void {
  if (!activeEmbeddedCheckout) return;
  try {
    activeEmbeddedCheckout.destroy();
  } catch (error) {
    console.warn("[Stripe checkout] Error destroying embedded checkout", error);
  } finally {
    activeEmbeddedCheckout = null;
  }
}

function deactivateAccessibilityHandlers(): void {
  if (checkoutIframeObserver) {
    checkoutIframeObserver.disconnect();
    checkoutIframeObserver = null;
  }
  if (checkoutEscapeHandler) {
    document.removeEventListener("keydown", checkoutEscapeHandler);
    checkoutEscapeHandler = null;
  }
  if (checkoutClickOutsideHandler && checkoutOverlay) {
    checkoutOverlay.removeEventListener("click", checkoutClickOutsideHandler);
  }
  checkoutClickOutsideHandler = null;
  checkoutOverlay = null;
}

export function hideStripeCheckoutMountElement(): void {
  const element = document.getElementById(STRIPE_EMBEDDED_CHECKOUT_MOUNT_ID);
  if (!element) return;

  deactivateAccessibilityHandlers();
  destroyEmbeddedCheckout();
  element.replaceChildren();
  element.hidden = true;
  element.style.display = "none";
  element.style.pointerEvents = "none";
  unlockBodyScroll();
  previouslyFocusedElement?.focus();
  previouslyFocusedElement = null;
}

export interface MountEmbeddedCheckoutParams {
  clientSecret: string;
  container: HTMLElement | string;
  onComplete?: () => void | Promise<void>;
}

export async function mountEmbeddedCheckout(
  params: MountEmbeddedCheckoutParams
): Promise<void> {
  const clientSecret = params.clientSecret?.trim();
  if (!clientSecret) {
    throw new Error("Stripe embedded checkout requires clientSecret");
  }

  destroyEmbeddedCheckout();

  const stripe = await getStripeJs();
  if (!stripe) {
    throw new Error("Failed to load Stripe.js");
  }

  const embeddedCheckout = await stripe.initEmbeddedCheckout({
    clientSecret,
    onComplete: () => {
      params.onComplete?.();
    },
  });

  activeEmbeddedCheckout = embeddedCheckout;
  embeddedCheckout.mount(params.container);
}
